<template>
    <li
        class="relative flex gap-x-4"
        data-spec-class="feed-comment"
        :data-spec-value="theme"
    >
        <div class="absolute top-0 -bottom-6 left-0 flex w-12 justify-center">
            <div class="w-px bg-secondary--inverse"></div>
        </div>

        <img
            v-if="avatarUrl"
            :src="avatarUrl"
            :alt="avatarAlt"
            class="relative mt-3 h-12 w-12 flex-none rounded-full bg-surface--light object-cover"
            data-spec-class="feed-comment__avatar"
        />

        <div
            v-else
            class="relative mt-3 flex h-12 w-12 flex-none items-center justify-center rounded-full bg-secondary--inverse"
        >
            <img
                v-if="theme === FeedCommentTheme.Agent"
                src="/img/23M-Logo.svg"
                alt="23M Logo"
                class="h-8 w-8 object-contain brightness-0 dark:invert"
            />
            <UserIcon
                v-else
                class="h-10 w-10 text-white dark:text-gray-300"
            />
        </div>

        <div :class="classObject">
            <div class="mb-1 flex justify-between gap-x-4">
                <div class="py-0.5 text-sm text-tertiary">
                    <span
                        class="font-medium text-primary"
                        :class="!authorName ? 'italic' : ''"
                        data-spec-class="feed-comment__author"
                    >
                        <template v-if="authorName">
                            {{ authorName }}
                        </template>
                        <template v-else>
                            {{ t('pages.TicketPage.deletedUser') }}
                        </template>
                    </span>
                    {{ t('pages.TicketPage.commented') }}
                </div>
                <div class="flex-none py-0.5 text-sm text-tertiary">
                    {{ diffInWords(date) }}
                </div>
            </div>
            <div
                v-if="props.contentType === TicketContentType.Plain"
                class="editor-content prose prose-sm max-w-none text-sm whitespace-pre-wrap text-secondary"
            >
                {{ props.comment }}
            </div>
            <div
                v-else
                ref="contentRef"
                class="editor-content prose prose-sm max-w-none text-sm text-secondary"
            >
                <!-- eslint-disable vue/no-v-html -->
                <div v-html="processedHtmlComment"></div>
                <!-- eslint-enable vue/no-v-html -->
            </div>
            <slot name="append"></slot>
        </div>
    </li>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, nextTick } from 'vue';
import { useCoreSetup } from '@/modules/your/composables/core';
import { UserIcon } from '@heroicons/vue/20/solid';
import { FeedCommentTheme, TicketContentType } from '@/types';
import { useI18n } from 'vue-i18n';
import { buildBase64ImageUrl } from '@/utils/fileUtils';
import { removeTrailingEmptyHtmlTags } from '@/utils/stringUtils';

const { t } = useI18n();
const { diffInWords } = useCoreSetup();

interface Props {
    authorName: string;
    date: string;
    contentType: TicketContentType;
    comment: string;
    avatarBase64?: string;
    avatarAlt?: string;
    theme?: FeedCommentTheme;
}

const props = withDefaults(defineProps<Props>(), {
    avatarBase64: undefined,
    avatarAlt: undefined,
    theme: FeedCommentTheme.Customer,
});

const avatarUrl = computed(() => {
    return buildBase64ImageUrl(props.avatarBase64);
});

const processedHtmlComment = computed(() => {
    return removeTrailingEmptyHtmlTags(props.comment);
});

const contentRef = ref<HTMLElement | null>(null);

const trackImageLoading = () => {
    if (!contentRef.value) {
        return;
    }

    const images = contentRef.value.querySelectorAll('img');

    images.forEach((img) => {
        if (img.complete || img.hasAttribute('data-spinner-added')) {
            if (img.complete) {
                img.classList.add('feed-comment__image-loaded');
            }
            return;
        }

        img.setAttribute('data-spinner-added', 'true');
        img.classList.add('feed-comment__image-loading');

        const wrapper = document.createElement('span');
        wrapper.className = 'relative inline-block';

        const spinner = document.createElement('span');
        spinner.className = 'absolute animate-pulse inset-0 z-10 flex items-center justify-center';
        spinner.setAttribute('data-spec-class', 'feed-comment__image-loading');
        spinner.innerHTML =
            '<span class="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-800 dark:border-gray-600 dark:border-t-blue-700"></span>';

        const parent = img.parentNode;
        if (parent) {
            parent.insertBefore(wrapper, img);
            wrapper.appendChild(img);
            wrapper.appendChild(spinner);

            const hideSpinner = () => {
                spinner.remove();
                img.classList.remove('feed-comment__image-loading');
                img.classList.add('feed-comment__image-loaded');
            };

            img.addEventListener('load', hideSpinner, { once: true });
            img.addEventListener('error', hideSpinner, { once: true });
        }
    });
};

watch(
    () => props.comment,
    async () => {
        await nextTick();
        trackImageLoading();
    },
    { immediate: false }
);

onMounted(async () => {
    await nextTick();
    trackImageLoading();
});

const classObject = {
    'flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200 dark:ring-gray-700': true,
    'bg-surface': props.theme === FeedCommentTheme.Customer,
    'bg-surface--light': props.theme === FeedCommentTheme.Agent,
};
</script>

<style scoped src="../../css/editor-content.css"></style>
